import {
  Button,
  ButtonGroup,
  closeDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Option,
  showNotification,
} from 'platform/components';
import {VStack} from 'platform/foundation';

import {
  useGetSelectableMaterialsIntegrationsQuery,
  usePostSelectableMaterialMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import {CREATE_SELECTABLE_MATERIAL_DIALOG} from '../constants/CreateSelectableMaterialDialog';
import {
  SelectableMaterialForm,
  SelectableMaterialFormSchema,
} from '../types/SelectableMaterialForm';

export function CreateSelectableMaterial() {
  const navigate = useNavigate();

  const integrationsQuery = useGetSelectableMaterialsIntegrationsQuery();

  const [postSelectableMaterial] = usePostSelectableMaterialMutation();

  const integrationOptions: Option[] =
    integrationsQuery.data?.map((integration) => ({
      value: integration.integrationId,
      label: integration.name,
    })) ?? [];

  const defaultValues: SelectableMaterialForm = {
    integrationId: '',
  };

  const handleSubmit: FormSubmitHandler<SelectableMaterialForm> = async (formValues) => {
    await postSelectableMaterial({
      integrationId: formValues.integrationId,
    })
      .unwrap()
      .then((result) => {
        navigate(
          composePath(settingsRoutes.workshopSelectableMaterialsDetail, {
            params: {id: result.selectableMaterialId},
          })
        );
      })
      .then(() => showNotification.success(i18n.t('general.notifications.successfullyCreated')))
      .catch(handleApiError);
  };

  return (
    <Form<SelectableMaterialForm>
      experimentalZodSchema={SelectableMaterialFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            isRequired
            isNotClearable
            isLoading={integrationsQuery.isLoading}
            control={control}
            name="integrationId"
            type="choice"
            label={i18n.t('general.labels.integration')}
            options={integrationOptions}
            menuInPortal
          />
          <ButtonGroup align="right">
            <Button
              type="button"
              variant="secondary"
              title={i18n.t('general.actions.discard')}
              onClick={() => closeDialog(CREATE_SELECTABLE_MATERIAL_DIALOG)}
            />
            <FormButton
              control={control}
              type="submit"
              variant="primary"
              title={i18n.t('general.actions.create')}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
